import React, { useEffect, useState, useCallback } from 'react';
import { usePage } from '../../hooks/usePage';
import { transactionsApi } from '../../api/transactions';
import { Transaction, PaginatedResponse } from '../../types';
import { Card } from '../../components/ui/Card';
import { Table } from '../../components/ui/Table';
import { Badge } from '../../components/ui/Badge';
import { Spinner } from '../../components/ui/Spinner';
import { Pagination } from '../../components/ui/Pagination';
import { TransactionDetail } from './TransactionDetail';
import {
  DateRangeFilter,
  DateRange,
  DateRangePreset,
  computeRange,
} from '../../components/ui/DateRangeFilter';

const statusColors: Record<string, 'success' | 'danger' | 'warning' | 'neutral'> = {
  success: 'success',
  failed: 'danger',
  pending: 'warning',
  reversed: 'neutral',
};

const typeColors: Record<string, 'info' | 'success' | 'neutral'> = {
  debit: 'info',
  credit: 'success',
  reversal: 'neutral',
};

const formatNaira = (amount: string | number) => {
  const n = typeof amount === 'string' ? parseFloat(amount) : amount;
  return `₦${n.toLocaleString('en-NG', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

const formatDate = (iso: string) =>
  new Date(iso).toLocaleString('en-NG', {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });

interface TransactionsProps {
  /**
   * Which ledger to read.
   *
   * `payments` and `business` are one table split by the CHECK constraint on
   * `transactions` — precisely one of `user_id` and `business_id` is set —
   * so the same screen serves both and no row appears on both. `shop` is
   * escrow holds against orders, mapped onto the same row shape.
   */
  source?: 'payments' | 'shop' | 'business';
}

export const Transactions: React.FC<TransactionsProps> = ({ source = 'payments' }) => {
  const isShop = source === 'shop';
  const isBusiness = source === 'business';
  const [search, setSearch] = useState('');

  usePage(
    isShop ? 'Shop Orders' : isBusiness ? 'Business Transactions' : 'Transactions',
    {
      placeholder: isShop
        ? 'Search by order reference or vendor…'
        : isBusiness
          ? 'Search by business, reference, account number, or name…'
          : 'Search by reference, account number, or name…',
      onSearch: (val) => setSearch(val),
    },
  );

  const [data, setData] = useState<PaginatedResponse<Transaction> | null>(null);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [typeFilter, setTypeFilter] = useState<string>('');
  const [dateRange, setDateRange] = useState<DateRange>(() => computeRange('6_months'));
  const [selectedTxId, setSelectedTxId] = useState<string | null>(null);

  const fetchTransactions = useCallback(async () => {
    try {
      setLoading(true);
      const result = await transactionsApi.getTransactions({
        source,
        search: search || undefined,
        status: statusFilter || undefined,
        type: isShop ? undefined : typeFilter || undefined,
        fromDate: dateRange.fromDate.toISOString(),
        toDate: dateRange.toDate.toISOString(),
        page,
        limit: 20,
      });
      setData(result);
    } catch {
      // error
    } finally {
      setLoading(false);
    }
  }, [source, search, statusFilter, typeFilter, isShop, dateRange, page]);

  useEffect(() => {
    setPage(1);
  }, [source, search, statusFilter, typeFilter, dateRange]);

  useEffect(() => {
    const timer = setTimeout(() => {
      void fetchTransactions();
    }, 250);
    return () => clearTimeout(timer);
  }, [fetchTransactions]);

  const handleRangeChange = useCallback((_preset: DateRangePreset, range: DateRange) => {
    setDateRange(range);
  }, []);

  const filterSelectStyle: React.CSSProperties = {
    height: 38,
    padding: '0 32px 0 12px',
    fontSize: 13,
    fontFamily: 'var(--font-family)',
    color: 'var(--neutral-700)',
    background: 'var(--neutral-100)',
    border: '1px solid var(--neutral-300)',
    borderRadius: 'var(--radius-md)',
    outline: 'none',
    appearance: 'none',
    backgroundImage:
      'url("data:image/svg+xml,%3Csvg xmlns=\'http://www.w3.org/2000/svg\' width=\'12\' height=\'12\' viewBox=\'0 0 12 12\' fill=\'none\'%3E%3Cpath d=\'M3 4.5L6 7.5L9 4.5\' stroke=\'%23566472\' stroke-width=\'1.5\' stroke-linecap=\'round\' stroke-linejoin=\'round\'/%3E%3C/svg%3E")',
    backgroundRepeat: 'no-repeat',
    backgroundPosition: 'right 10px center',
    cursor: 'pointer',
  };

  return (
    <>
      <Card padding={20}>
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-start',
            gap: 12,
            marginBottom: 20,
            flexWrap: 'wrap',
          }}
        >
          <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              style={filterSelectStyle}
            >
              <option value="">All Statuses</option>
              <option value="success">Success</option>
              <option value="pending">Pending</option>
              {!isShop ? <option value="failed">Failed</option> : null}
              <option value="reversed">Reversed</option>
            </select>
            {!isShop ? (
              <select
                value={typeFilter}
                onChange={(e) => setTypeFilter(e.target.value)}
                style={filterSelectStyle}
              >
                <option value="">All Types</option>
                <option value="debit">Debit</option>
                <option value="credit">Credit</option>
                <option value="reversal">Reversal</option>
              </select>
            ) : null}
          </div>
          <DateRangeFilter defaultPreset="6_months" onChange={handleRangeChange} />
        </div>

        {loading ? (
          <div style={{ display: 'flex', justifyContent: 'center', padding: 48 }}>
            <Spinner size={24} />
          </div>
        ) : data ? (
          <>
            <Table
              data={data.items}
              onRowClick={isShop ? undefined : (tx) => setSelectedTxId(tx.id)}
              columns={[
                {
                  key: 'reference',
                  header: 'Reference',
                  render: (t) => (
                    <span
                      style={{
                        fontFamily: 'monospace',
                        fontSize: 12,
                        color: 'var(--neutral-700)',
                      }}
                    >
                      {t.reference.slice(0, 12)}…
                    </span>
                  ),
                },
                {
                  key: 'type',
                  header: isShop ? 'Source' : 'Type',
                  render: (t) =>
                    isShop ? (
                      <Badge variant="info">shop</Badge>
                    ) : (
                      <Badge variant={typeColors[t.type]}>{t.type}</Badge>
                    ),
                },
                {
                  key: 'amount',
                  header: 'Amount',
                  render: (t) => (
                    <span style={{ fontWeight: 600 }}>{formatNaira(t.amount)}</span>
                  ),
                },
                {
                  key: 'fee',
                  header: 'Fee',
                  render: (t) => (
                    <span style={{ color: 'var(--neutral-600)' }}>
                      {formatNaira(t.totalFee)}
                    </span>
                  ),
                },
                ...(isBusiness
                  ? [
                      {
                        key: 'business',
                        header: 'Business',
                        render: (t: Transaction) => (
                          <span style={{ fontWeight: 500, color: 'var(--neutral-800)' }}>
                            {t.businessName ?? '—'}
                          </span>
                        ),
                      },
                    ]
                  : []),
                {
                  key: 'counterparty',
                  header: isShop ? 'Vendor' : 'Counterparty',
                  render: (t) => (
                    <div>
                      <div style={{ fontWeight: 500, color: 'var(--neutral-800)' }}>
                        {t.accountName}
                      </div>
                      <div style={{ fontSize: 12, color: 'var(--neutral-500)' }}>
                        {t.bankName}
                      </div>
                    </div>
                  ),
                },
                {
                  key: 'status',
                  header: 'Status',
                  render: (t) => <Badge variant={statusColors[t.status]}>{t.status}</Badge>,
                },
                {
                  key: 'created',
                  header: 'Date',
                  render: (t) => (
                    <span style={{ fontSize: 13, color: 'var(--neutral-600)' }}>
                      {formatDate(t.createdAt)}
                    </span>
                  ),
                },
              ]}
              emptyMessage={
                search || statusFilter || (!isShop && typeFilter)
                  ? 'No transactions match your filters'
                  : 'No transactions in this date range'
              }
            />
            <Pagination
              page={data.meta.page}
              totalPages={data.meta.totalPages}
              total={data.meta.total}
              onPageChange={setPage}
            />
          </>
        ) : null}
      </Card>

      {!isShop ? (
        <TransactionDetail
          transactionId={selectedTxId}
          isOpen={selectedTxId !== null}
          onClose={() => setSelectedTxId(null)}
        />
      ) : null}
    </>
  );
};
